import { DistanceFieldGenerator } from "gpu-distance-field";

// Returns the distance from a particular coordinate
// on the canvas to the nearest shape, as determined
// by the distance field.
//
// `pixels` is the output of calling
// `DistanceFieldGenerator.getPixels()`.
function getDistanceFromPixels(
  pixels: Uint8Array,
  canvasWidth: number,
  canvasHeight: number,
  xInput: number,
  yInput: number
) {
  const x = xInput | 0;
  const y = yInput | 0;

  if (pixels.length != canvasWidth * canvasHeight * 4) {
    throw new Error("Unexpected pixels size");
  }

  // In WebGL the y-coordinate increases as you go up
  // on your screen and in the DOM the y-coordinate
  // decreases as you go up on your screen. `pixels`
  // came right out of a WebGL texture, we need to
  // flip it.
  const flippedY = canvasHeight - y;

  // `pixels` is an array of r, g, b, a values between
  // 0 and 255. Figure out the index of the red value in
  // the pixel we want.
  const BYTES_PER_PIXEL = 4;
  const pixelsPerRow = canvasWidth * BYTES_PER_PIXEL;
  const redPixelIndex = flippedY * pixelsPerRow + x * BYTES_PER_PIXEL;

  // Pull out the values of the red pixel, along with
  // green and blue. alpha isn't used.
  const r = pixels[redPixelIndex];
  const g = pixels[redPixelIndex + 1];
  const b = pixels[redPixelIndex + 2];
  if (isNaN(r) || isNaN(g) || isNaN(b)) {
    throw new Error(`AHH`);
  }

  // The rgb values of each pixel store the distance as
  // a base 255 number added to BASE ^ 3 / 2 and multiplied
  // by 1000.
  const BASE = 255;
  const BASE_2 = BASE * BASE;
  const BASE_3 = BASE_2 * BASE;
  return (r * BASE_2 + g * BASE + b - BASE_3 / 2) / 1000;
}

export class DistanceField {
  private _inputCanvas: HTMLCanvasElement;
  private _jumpFlood: DistanceFieldGenerator;
  private _needsUpdate = false;

  constructor(inputCanvas: HTMLCanvasElement) {
    this._inputCanvas = inputCanvas;

    this._jumpFlood = new DistanceFieldGenerator({
      sizeHint: [inputCanvas.width, inputCanvas.height],
    });
  }

  canvas() {
    return this._jumpFlood.outputCanvas();
  }

  setNeedsUpdate() {
    this._needsUpdate = true;
  }

  needsUpdate() {
    return this._needsUpdate;
  }

  updateForScreenResize() {
    this._needsUpdate = true;
  }

  update() {
    this._jumpFlood.generateSDF(this._inputCanvas, "JFA");
    this._needsUpdate = false;
  }

  cpuSampler() {
    const pixels = this._jumpFlood.getPixels();
    const canvasWidth = this._inputCanvas.width;
    const canvasHeight = this._inputCanvas.height;
    return (x: number, y: number) => {
      return getDistanceFromPixels(pixels, canvasWidth, canvasHeight, x, y);
    };
  }
}
